
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MessageCircle, Phone, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SessionBookingWithMentor } from '@/utils/menteeBookingsUtils';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useDailyVideoCall } from '@/hooks/useDailyVideoCall';
import { useCallDurationManager } from '@/hooks/useCallDurationManager';
import { DailyCallManager } from '@/components/DailyCallManager';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getUserTimezone, convertFromUTC, getTimezoneAbbreviation } from '@/utils/timezoneUtils';

interface MenteeAppointmentDetailsDialogProps {
  booking: SessionBookingWithMentor;
  isOpen: boolean;
  onClose: () => void;
}

const MenteeAppointmentDetailsDialog = ({ booking, isOpen, onClose }: MenteeAppointmentDetailsDialogProps) => {
  const [courseType, setCourseType] = useState<string | null>(null);
  const { findOrCreateVideoRoom, startVideoCall, isCreatingRoom } = useDailyVideoCall();
  const { isCallActive, callDuration, startCallTimer, endCallTimer } = useCallDurationManager();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [callInProgress, setCallInProgress] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userTimezone] = useState<string>(getUserTimezone());

  // Fetch course type when dialog opens
  useEffect(() => {
    if (isOpen && booking.application_id) {
      console.log('Fetching course type for mentee booking:', booking.id, 'application_id:', booking.application_id);
      fetchCourseType();
    } else {
      console.log('Not fetching course type. isOpen:', isOpen, 'application_id:', booking.application_id);
    }
  }, [isOpen, booking.application_id]);

  // Update current time every minute when dialog is open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isOpen]);

  const fetchCourseType = async () => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          courses (
            course_type
          )
        `)
        .eq('id', booking.application_id)
        .single();

      if (error) {
        console.error('Error fetching course type:', error);
        return;
      }

      if (data?.courses) {
        console.log('Course type fetched for mentee:', data.courses.course_type);
        setCourseType(data.courses.course_type);
      } else {
        console.log('No course data found for mentee');
      }
    } catch (error) {
      console.error('Error in fetchCourseType for mentee:', error);
    }
  };

  // Helper function to determine if message button should be shown
  const shouldShowMessageButton = () => {
    return courseType === 'subscription';
  };

  const handleMessageMentor = async () => {
    if (!user) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to send messages.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if conversation already exists
      const { data: existingConversation, error: fetchError } = await supabase
        .from('conversations')
        .select('id')
        .eq('mentee_id', user.id)
        .eq('mentor_id', booking.mentor_id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error checking existing conversation:', fetchError);
        toast({
          title: "Error",
          description: "Failed to check existing conversation. Please try again.",
          variant: "destructive",
        });
        return;
      }

      let conversationId = existingConversation?.id;

      // If no conversation exists, create one
      if (!conversationId) {
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            mentor_id: booking.mentor_id,
            mentee_id: user.id,
          })
          .select('id')
          .single();

        if (createError) {
          console.error('Error creating conversation:', createError);
          toast({
            title: "Error",
            description: "Failed to create conversation. Please try again.",
            variant: "destructive",
          });
          return;
        }

        conversationId = newConversation.id;
      }

      // Navigate to the conversation
      navigate(`/messages/${conversationId}`);
      onClose();
    } catch (error) {
      console.error('Error in handleMessageMentor:', error);
      toast({
        title: "Error",
        description: "Failed to start conversation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleStartCall = async () => {
    try {
      setCallInProgress(true);
      
      console.log('Starting video call for booking:', booking.id);
      
      // Find or create Daily.co room using the shared room logic
      const sessionData = await findOrCreateVideoRoom({
        mentorId: booking.mentor_id,
        menteeId: booking.mentee_id,
        bookingId: booking.id
      });

      if (sessionData) {
        // Start the call duration timer
        await startCallTimer(booking.mentor_id, booking.mentee_id, booking.booked_date);
        
        // Open the video call
        startVideoCall(sessionData.room_url, () => {
          console.log('Call started, duration tracking active');
        });
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      toast({
        title: "Error",
        description: "Failed to start video call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCallInProgress(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if call button should be enabled based on time
  const getCallButtonState = () => {
    if (booking.status !== 'confirmed') {
      return { enabled: false, tooltip: 'Appointment must be confirmed to start call' };
    }

    try {
      // ✅ FIXED: Convert UTC booking times to local timezone
      const startLocal = convertFromUTC(
        booking.booked_date,
        booking.start_time,
        userTimezone
      );

      const endLocal = convertFromUTC(
        booking.booked_date,
        booking.end_time,
        userTimezone
      );

      const now = currentTime;

      if (now < startLocal.localDate) {
        return { 
          enabled: false, 
          tooltip: `The call will be available at ${startLocal.localTime}` 
        };
      } else if (now > endLocal.localDate) {
        return { 
          enabled: false, 
          tooltip: `This call ended at ${endLocal.localTime}` 
        };
      } else {
        return { 
          enabled: true, 
          tooltip: 'Click to start your scheduled call' 
        };
      }
    } catch (error) {
      console.error('Error parsing appointment times:', error);
      return { enabled: false, tooltip: 'Invalid appointment time format' };
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-bold">
              <Calendar className="h-6 w-6 text-emu-blue" />
              Appointment Details
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Mentor Information */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={booking.mentor.avatar_url} />
                  <AvatarFallback className="text-sm">
                    {booking.mentor.first_name[0]}{booking.mentor.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {booking.mentor.first_name} {booking.mentor.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">Mentor</p>
                </div>
              </div>
              <Badge className={`${getStatusColor(booking.status)} text-xs px-2 py-1`}>
                {booking.status}
              </Badge>
            </div>

            {/* Date and Time Details */}
            {(() => {
              const bookingLocal = convertFromUTC(
                booking.booked_date,
                booking.start_time,
                userTimezone
              );

              const endLocal = convertFromUTC(
                booking.booked_date,
                booking.end_time,
                userTimezone
              );

              return (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Date</p>
                        <p className="text-sm font-medium">
                          {format(bookingLocal.localDate, 'EEEE, MMMM d, yyyy')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Time</p>
                        <p className="text-sm font-medium">
                          {bookingLocal.localTime} - {endLocal.localTime}
                          <Badge variant="secondary" className="text-xs ml-2">
                            {getTimezoneAbbreviation(userTimezone)}
                          </Badge>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Notes Section */}
            {booking.notes && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                <p className="text-gray-700 text-sm">{booking.notes}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t gap-3">
              <div className="flex gap-2">
                {shouldShowMessageButton() && (
                  <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={handleMessageMentor}>
                    <MessageCircle className="h-3 w-3 mr-2" />
                    Message Mentor
                  </Button>
                )}
                {booking.status === 'confirmed' && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1">
                          <HelpCircle className="h-3 w-3 text-gray-400" />
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8 px-3 text-xs"
                            onClick={handleStartCall}
                            disabled={isCreatingRoom || callInProgress || !getCallButtonState().enabled}
                          >
                            <Phone className="h-3 w-3 mr-2" />
                            {isCreatingRoom || callInProgress ? 'Starting Call...' : 'Start Call'}
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">{getCallButtonState().tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              
              <Button variant="outline" onClick={onClose} size="sm" className="h-8 px-3 text-xs">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DailyCallManager
        isCallActive={isCallActive}
        expiryTimestamp={undefined}
        onCallEnd={endCallTimer}
      />
    </>
  );
};

export default MenteeAppointmentDetailsDialog;
